// Which delivery days exist for a country, as seen by the signed-in user. A thin React binding
// over availabilityStore: the store owns fetching, de-duplication and the TTL, this hook only
// subscribes to it and asks it to fetch.
import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import type { CountryCode } from '../config/countries';
import { useAuth } from '../contexts/AuthContext';
import { availabilityStore } from '../services/availabilityStore';
import type { AvailabilityState } from '../services/availabilityStore';

// One shared object, so useSyncExternalStore sees the same snapshot on every render while there
// is nothing to read (no user yet, or no country).
const IDLE: AvailabilityState = Object.freeze({ status: 'idle' as const });

/**
 * State of `country`'s delivery dates for the signed-in user. `country` or the user being null
 * gives `{ status: 'idle' }` and fetches nothing. The fetch starts once per user and country
 * however many components ask, and again only after the store's TTL; React.StrictMode's second
 * effect run joins the fetch already in flight. `retry` drops the entry and fetches again.
 */
export function useAvailability(country: CountryCode | null): { state: AvailabilityState; retry: () => void } {
  const uid = useAuth().currentUser?.uid ?? null;
  const { get, ensure, refresh, subscribe } = availabilityStore;

  useEffect(() => {
    if (uid && country) ensure(uid, country);
  }, [uid, country, ensure]);

  const state = useSyncExternalStore(
    subscribe,
    () => (uid && country ? get(uid, country) : IDLE),
    () => IDLE,
  );

  const retry = useCallback(() => {
    if (uid && country) refresh(uid, country);
  }, [uid, country, refresh]);

  return useMemo(() => ({ state, retry }), [state, retry]);
}
